/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { motion } from 'motion/react';
import { X, Lock, KeyRound, ArrowRight, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import userService from '../api/userService';
import { useAuth } from './AuthContext';
import Modal from './Modal';

export default function ChangePasswordModal({ isOpen, onClose }) {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess(false);
      setIsLoading(false);
      setShowCurrent(false);
      setShowNew(false);
      setShowConfirm(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Nova lozinka mora imati najmanje 6 karaktera.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Nova lozinka i potvrda se ne poklapaju.');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Nova lozinka mora biti različita od trenutne.');
      return;
    }

    const email = user?.email || '';
    if (!email) {
      setError('Niste prijavljeni — nedostaje email.');
      return;
    }

    setIsLoading(true);
    try {
      await userService.changePassword(email, currentPassword, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Greška pri izmeni lozinke.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} label="Izmena lozinke">
      <button
        onClick={onClose}
        aria-label="Zatvori"
        className="absolute top-6 right-6 z-10 p-2 text-sage-400 hover:text-sage-600 hover:bg-sage-50 hover:scale-110 rounded-full transition-all"
      >
        <X size={20} />
      </button>
            <div className="p-8 sm:p-10 max-h-[88dvh] overflow-y-auto custom-scrollbar">

              <div className="mb-8">
                <div className="w-14 h-14 rounded-2xl bg-sage-100 text-sage-700 flex items-center justify-center mb-4">
                  <KeyRound size={26} />
                </div>
                <h2 className="text-3xl font-serif font-bold text-earth-900 mb-2">
                  Izmena lozinke
                </h2>
                <p className="text-sage-600">
                  Unesite trenutnu lozinku i izaberite novu.
                </p>
              </div>

              {success ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-sage-50 p-6 rounded-3xl border border-sage-100 text-center"
                >
                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-sage-600 mx-auto mb-4 shadow-sm">
                    <CheckCircle size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-earth-900 mb-2">Lozinka izmenjena</h3>
                  <p className="text-sage-600 text-sm mb-6">
                    Vaša lozinka je uspešno ažurirana.
                  </p>
                  <button
                    onClick={onClose}
                    className="text-sage-700 font-bold hover:text-sage-900 hover:underline hover:scale-105 transition-all"
                  >
                    Zatvori
                  </button>
                </motion.div>
              ) : (
                <form className="space-y-4" onSubmit={handleSubmit}>
                  <div>
                    <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Trenutna lozinka</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                      <input
                        type={showCurrent ? 'text' : 'password'}
                        required
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        placeholder="••••••••"
                        className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-12 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrent((v) => !v)}
                        tabIndex={-1}
                        aria-label={showCurrent ? 'Sakrij lozinku' : 'Prikaži lozinku'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-sage-400 hover:text-sage-700 transition-colors"
                      >
                        {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Nova lozinka</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                      <input
                        type={showNew ? 'text' : 'password'}
                        required
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="Najmanje 6 karaktera"
                        className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-12 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNew((v) => !v)}
                        tabIndex={-1}
                        aria-label={showNew ? 'Sakrij lozinku' : 'Prikaži lozinku'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-sage-400 hover:text-sage-700 transition-colors"
                      >
                        {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-earth-900 mb-1.5 ml-1">Potvrdi novu lozinku</label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-sage-400" size={18} />
                      <input
                        type={showConfirm ? 'text' : 'password'}
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Ponovi novu lozinku"
                        className="w-full bg-sage-50 border border-sage-100 rounded-2xl py-3.5 pl-12 pr-12 focus:ring-2 focus:ring-sage-200 outline-none transition-all"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirm((v) => !v)}
                        tabIndex={-1}
                        aria-label={showConfirm ? 'Sakrij lozinku' : 'Prikaži lozinku'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-sage-400 hover:text-sage-700 transition-colors"
                      >
                        {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-2xl px-4 py-3">
                      {error}
                    </div>
                  )}

                  <motion.button
                    whileHover={!isLoading ? { scale: 1.03 } : {}}
                    whileTap={!isLoading ? { scale: 0.98 } : {}}
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-sage-600 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 shadow-lg shadow-sage-600/20 hover:bg-sage-700 hover:shadow-xl hover:shadow-sage-700/30 transition-all mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Slanje...' : 'Sačuvaj novu lozinku'}
                    {!isLoading && <ArrowRight size={20} />}
                  </motion.button>
                </form>
              )}
            </div>
    </Modal>
  );
}
